import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const Settings = () => {
  const { t } = useTranslation();
  const {
    user,
    isGoogleAuthenticated: isGoogleConnected,
    googleUserInfo,
    googleLogin: connectGoogle,
    googleLogout: disconnectGoogle,
    isGoogleLoading: isLoading
  } = useAuth();

  const handleDisconnect = async () => {
    if (window.confirm(t('settings.googleDrive.confirmDisconnect'))) {
      try {
        await disconnectGoogle();
      } catch (error) {
        console.error('Error disconnecting Google account:', error);
        // Toast notification is already shown from AuthContext
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('settings.title')}</h1>

        {/* Account Information Section */}
        <section className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{t('settings.account.title')}</h2>
          </div>
          <div className="px-6 py-5">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.account.name')}</label>
                <p className="text-gray-900">{user?.name || t('settings.account.notSet')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.account.email')}</label>
                <p className="text-gray-900">{user?.email}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Google Drive Integration Section */}
        <section className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{t('settings.googleDrive.title')}</h2>
            <p className="mt-1 text-sm text-gray-600">
              {t('settings.googleDrive.description')}
            </p>
          </div>
          <div className="px-6 py-5">
            {isGoogleConnected ? (
              <div className="space-y-4">
                {/* Connection Status */}
                <div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {t('settings.googleDrive.connected')}
                  </span>
                </div>

                {/* Connected Account Display */}
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  {googleUserInfo?.picture && (
                    <img
                      src={googleUserInfo.picture}
                      alt={googleUserInfo.name}
                      className="w-12 h-12 rounded-full border-2 border-gray-200"
                    />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{googleUserInfo?.name}</p>
                    <p className="text-sm text-gray-600">{googleUserInfo?.email}</p>
                  </div>
                </div>

                {/* Disconnect Button */}
                <div className="pt-2">
                  <button
                    onClick={handleDisconnect}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t('settings.googleDrive.disconnect')}
                  </button>
                </div>

                {/* Warning Message */}
                <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        Disconnecting will prevent you from editing documents in Google Docs. Your existing Google Docs will remain in your Google Drive.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Not Connected Status */}
                <div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Not Connected
                  </span>
                </div>

                {/* Info Box */}
                <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        Connect your Google account to edit translated documents in Google Docs and save them to your Google Drive.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Connect Button */}
                <div>
                  <button
                    onClick={connectGoogle}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      className="w-5 h-5 mr-2"
                    />
                    Connect Google Account
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Future Sections Placeholder */}
        <section className="bg-white rounded-lg shadow">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Preferences</h2>
          </div>
          <div className="px-6 py-5">
            <p className="text-gray-600">Additional settings coming soon...</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Settings;
